import { useState, useCallback, useMemo } from 'react'
import {
  SoapTemplateGroup,
  SoapTemplateItem,
  SoapType,
} from '@/features/internal/drugs/types/soapTemplate'

/**
 * テンプレートアイテムにIDがない場合、一時的なIDを生成する
 */
const ensureTemplateIds = (
  groups: SoapTemplateGroup[]
): SoapTemplateGroup[] => {
  return groups.map((group) => ({
    ...group,
    templates: group.templates.map((item) => {
      if (!item.id) {
        return {
          ...item,
          id: `temp-item-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        }
      }
      return item
    }),
  }))
}

interface UseSoapTemplateEditorOptions {
  /**
   * 初期データ（サーバーから取得したテンプレート）
   */
  initialData?: SoapTemplateGroup[]
}

interface UseSoapTemplateEditorReturn {
  // 状態
  editingTemplateGroups: SoapTemplateGroup[]
  hasChanges: boolean

  // 状態セッター（直接操作が必要な場合）
  setEditingTemplateGroups: React.Dispatch<
    React.SetStateAction<SoapTemplateGroup[]>
  >

  // リセット操作
  resetChanges: () => void
  initializeFromData: (data: SoapTemplateGroup[]) => void

  // グループ操作
  addTemplateGroup: () => void
  removeTemplateGroup: (groupIndex: number) => void
  updateTemplateGroupName: (groupIndex: number, newName: string) => void
  updateTemplateGroupKeywords: (groupIndex: number, keywords: string[]) => void

  // アイテム操作
  addTemplateItem: (groupIndex: number, soapType: SoapType) => void
  removeTemplateItem: (groupIndex: number, itemIndex: number) => void
  updateTemplateItem: (
    groupIndex: number,
    itemIndex: number,
    newContent: string
  ) => void
}

/**
 * SOAPテンプレートのローカル編集状態を管理するフック
 *
 * 責務:
 * - 編集中のテンプレートデータの状態管理
 * - 変更有無のトラッキング
 * - ローカル編集操作（追加、削除、更新）
 *
 * 使用例:
 * ```tsx
 * const { templateGroups } = useDrugSoapTemplatesQuery(drugId)
 * const editor = useSoapTemplateEditor({ initialData: templateGroups })
 *
 * // 編集操作
 * editor.addTemplateGroup()
 * editor.updateTemplateItem(0, 0, '新しい内容')
 *
 * // 保存時
 * await updateMutation.mutateAsync({
 *   drugId,
 *   templateGroups: editor.editingTemplateGroups,
 * })
 * ```
 */
export const useSoapTemplateEditor = (
  options: UseSoapTemplateEditorOptions = {}
): UseSoapTemplateEditorReturn => {
  const { initialData = [] } = options

  // 初期データを処理(メモ化)
  const processedInitialData = useMemo(() => {
    if (initialData.length === 0) return []
    return ensureTemplateIds(initialData)
  }, [initialData])

  // 編集中のテンプレートグループ(遅延初期化)
  const [editingTemplateGroups, setEditingTemplateGroups] = useState<
    SoapTemplateGroup[]
  >(() => {
    if (processedInitialData.length > 0) {
      return JSON.parse(JSON.stringify(processedInitialData))
    }
    return []
  })

  // オリジナルデータ（リセット用、遅延初期化）
  const [originalData, setOriginalData] = useState<SoapTemplateGroup[]>(() => {
    return processedInitialData.length > 0 ? processedInitialData : []
  })

  // 変更フラグ
  const [hasChanges, setHasChanges] = useState(false)

  // 外部からデータを初期化(イベントハンドラとして使う)
  const initializeFromData = useCallback((data: SoapTemplateGroup[]) => {
    const processed = ensureTemplateIds(data)
    setOriginalData(processed)
    setEditingTemplateGroups(JSON.parse(JSON.stringify(processed)))
    setHasChanges(false)
  }, [])

  // 編集をリセット（元の状態に戻す）
  const resetChanges = useCallback(() => {
    const copied = JSON.parse(JSON.stringify(originalData))
    setEditingTemplateGroups(ensureTemplateIds(copied))
    setHasChanges(false)
  }, [originalData])

  // ========================================
  // グループ操作
  // ========================================

  // 新しいテンプレートグループを追加
  const addTemplateGroup = useCallback(() => {
    const newGroup: SoapTemplateGroup = {
      id: `temp-${Date.now()}`,
      name: '新しいグループ',
      keywords: [],
      templates: [],
    }
    setEditingTemplateGroups((prev) => [...prev, newGroup])
    setHasChanges(true)
  }, [])

  // テンプレートグループを削除
  const removeTemplateGroup = useCallback((groupIndex: number) => {
    setEditingTemplateGroups((prev) => {
      const newGroups = [...prev]
      newGroups.splice(groupIndex, 1)
      return newGroups
    })
    setHasChanges(true)
  }, [])

  // テンプレートグループ名を更新
  const updateTemplateGroupName = useCallback(
    (groupIndex: number, newName: string) => {
      setEditingTemplateGroups((prev) => {
        const newGroups = [...prev]
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          name: newName,
        }
        return newGroups
      })
      setHasChanges(true)
    },
    []
  )

  // テンプレートグループのキーワードを更新
  const updateTemplateGroupKeywords = useCallback(
    (groupIndex: number, keywords: string[]) => {
      setEditingTemplateGroups((prev) => {
        const newGroups = [...prev]
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          keywords,
        }
        return newGroups
      })
      setHasChanges(true)
    },
    []
  )

  // ========================================
  // アイテム操作
  // ========================================

  // テンプレート項目を追加
  const addTemplateItem = useCallback(
    (groupIndex: number, soapType: SoapType) => {
      const newItem: SoapTemplateItem = {
        id: `temp-item-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        soap_type: soapType,
        content: '',
      }
      setEditingTemplateGroups((prev) => {
        const newGroups = [...prev]
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          templates: [...newGroups[groupIndex].templates, newItem],
        }
        return newGroups
      })
      setHasChanges(true)
    },
    []
  )

  // テンプレート項目を削除
  const removeTemplateItem = useCallback(
    (groupIndex: number, itemIndex: number) => {
      setEditingTemplateGroups((prev) => {
        const newGroups = [...prev]
        const newTemplates = [...newGroups[groupIndex].templates]
        newTemplates.splice(itemIndex, 1)
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          templates: newTemplates,
        }
        return newGroups
      })
      setHasChanges(true)
    },
    []
  )

  // テンプレート項目を更新
  const updateTemplateItem = useCallback(
    (groupIndex: number, itemIndex: number, newContent: string) => {
      setEditingTemplateGroups((prev) => {
        const newGroups = [...prev]
        const newTemplates = [...newGroups[groupIndex].templates]
        newTemplates[itemIndex] = {
          ...newTemplates[itemIndex],
          content: newContent,
        }
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          templates: newTemplates,
        }
        return newGroups
      })
      setHasChanges(true)
    },
    []
  )

  return {
    // 状態
    editingTemplateGroups,
    hasChanges,

    // 状態セッター
    setEditingTemplateGroups,

    // リセット操作
    resetChanges,
    initializeFromData,

    // グループ操作
    addTemplateGroup,
    removeTemplateGroup,
    updateTemplateGroupName,
    updateTemplateGroupKeywords,

    // アイテム操作
    addTemplateItem,
    removeTemplateItem,
    updateTemplateItem,
  }
}
